import { useCallback } from "react";
import { toast } from "sonner";

import { downloadFileWithFeedback } from "@/domain/_shared/file/lib/file-download";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQueryClient } from "@tanstack/react-query";

import { useMarkDashboardExportDownloaded } from "./api/use-mark-dashboard-export-downloaded";

interface ExportTarget {
  workspaceId: string;
  dashboardId: string;
  exportId: string;
}

interface DownloadExportInput extends ExportTarget {
  fileId: string;
  fileName: string;
}

export function useDashboardExportDownload(): {
  download: (input: DownloadExportInput) => Promise<void>;
  dismiss: (input: ExportTarget) => Promise<void>;
} {
  const { t } = useDomainTranslation("public-dashboard");
  const queryClient = useQueryClient();
  const markDownloaded = useMarkDashboardExportDownloaded();
  const { mutateAsync } = markDownloaded;

  const markHandled = useCallback(
    async ({ workspaceId, dashboardId, exportId }: ExportTarget) => {
      try {
        await mutateAsync({ workspaceId, id: exportId });
        await queryClient.invalidateQueries({ queryKey: queryKeys.dashboardExports.latest(workspaceId, dashboardId) });
        await queryClient.invalidateQueries({ queryKey: queryKeys.dashboardExports.lists(workspaceId) });
      } catch {
        toast.error(t("export.statusUpdateFailedTitle"), { description: t("export.statusUpdateFailedDescription") });
      }
    },
    [mutateAsync, queryClient, t],
  );

  const download = useCallback(
    async ({ workspaceId, dashboardId, exportId, fileId, fileName }: DownloadExportInput) => {
      const showDownloadError = () => {
        toast.error(t("export.downloadFailedTitle"), { description: t("export.downloadFailedDescription") });
      };
      const succeeded = await downloadFileWithFeedback({ fileId, fileName, onError: showDownloadError });
      if (!succeeded) {
        return;
      }
      await markHandled({ workspaceId, dashboardId, exportId });
    },
    [t, markHandled],
  );

  const dismiss = useCallback((input: ExportTarget) => markHandled(input), [markHandled]);

  return { download, dismiss };
}
